import React, { useMemo } from "react";
import { alpha } from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import color from "../../../Constants/colors";
import AmbassadorThemeProvider from "../../Ambassador/Common/AmbassadorThemeProvider";
import { useDashboardColors } from "../../HomePage/dashboardColors";

interface Props {
  children: React.ReactNode;
}

// The parts the Ambassador theme leaves to the app theme, which paints them
// for dark surfaces (accent sort labels, light icons and toggles).
const Extensions: React.FC<Props> = ({ children }) => {
  const outer = useTheme();
  const { primary, accent, onAccent } = useDashboardColors();

  const theme = useMemo(
    () =>
      createTheme(outer, {
        components: {
          MuiAutocomplete: {
            styleOverrides: {
              paper: { backgroundColor: color.white, color: color.gray900 },
              option: { color: color.gray900, fontSize: "0.875rem" },
              noOptions: { color: color.gray500, fontSize: "0.875rem" },
              loading: { color: color.gray500, fontSize: "0.875rem" },
              groupLabel: {
                color: color.gray500,
                fontWeight: 700,
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              },
              popupIndicator: { color: color.gray500 },
              clearIndicator: { color: color.gray500 },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                color: color.gray700,
                "&.Mui-disabled": { color: color.gray300 },
              },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: {
                color: color.gray700,
                borderColor: color.gray300,
                textTransform: "none",
                fontWeight: 600,
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: accent,
                  color: onAccent,
                },
              },
            },
          },
          MuiTableSortLabel: {
            styleOverrides: {
              root: {
                color: `${color.gray500} !important`,
                "&:hover, &.Mui-active": { color: `${primary} !important` },
                "& .MuiTableSortLabel-icon": {
                  color: `${color.gray400} !important`,
                },
              },
            },
          },
          MuiPaginationItem: {
            styleOverrides: {
              root: { color: color.gray700 },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: { fontSize: "0.75rem", backgroundColor: color.gray900 },
              arrow: { color: color.gray900 },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              root: { borderRadius: 4, backgroundColor: alpha(accent, 0.18) },
              bar: { borderRadius: 4 },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: { color: color.gray900, fontWeight: 700 },
            },
          },
          MuiSwitch: {
            styleOverrides: {
              switchBase: {
                "&.Mui-checked": { color: accent },
                "&.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: accent,
                },
              },
            },
          },
        },
      }),
    [outer, primary, accent, onAccent],
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

/**
 * Light theme of the Document Crafting pages: the Ambassador Workspace theme
 * (white cards, workspace accent as primary colour) plus the components only
 * this module uses.
 */
const DocumentCraftingThemeProvider: React.FC<Props> = ({ children }) => (
  <AmbassadorThemeProvider>
    <Extensions>{children}</Extensions>
  </AmbassadorThemeProvider>
);

export default DocumentCraftingThemeProvider;
